import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectBuchungen } from 'modules/finanzen/features/finanzenSlice';
import { barvermoegenVon, buchungenOhneOrt, summen } from 'modules/finanzen/features/berechnung';
import {
  selectInvestitionen,
  selectInvestitionenStatus,
  selectDepotWerte,
} from 'modules/investitionen/features/investitionenSlice';
import { bereichSummen } from 'modules/investitionen/features/berechnung';
import { formatKurs } from 'core/utils/format';
import BackToOverview from 'core/components/BackToOverview';
import GesichertGate from 'modules/finanzen/components/GesichertGate';
import ModulTabs from 'modules/finanzen/components/ModulTabs';
import GewinnBadge from 'modules/investitionen/components/GewinnBadge';
import KategorieDonut from 'modules/investitionen/components/KategorieDonut';
import InvestitionForm from 'modules/investitionen/components/InvestitionForm';

/**
 * Startseite des Finanzen-Moduls: Gesamtvermoegen (Barvermoegen + Investiert),
 * Verteilung je Investitionsort und die Orte als Karten. Nur im gesicherten Modus.
 */
export default function UebersichtPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6">
        <BackToOverview />
      </div>
      <GesichertGate>
        <Uebersicht />
      </GesichertGate>
    </div>
  );
}

/** Baut aus Orten (Investitionen) und Buchungen die kombinierten Kennzahlen je Ort. */
const kennzahlenJeOrt = (orte, buchungen, depotWerte) =>
  orte.map((ort) => {
    const bar = barvermoegenVon(ort, buchungen);
    const { investiert, wert, gv, gvProz } = bereichSummen(ort, depotWerte);
    return { ort, bar, investiert, wert, gv, gvProz, gesamt: bar + wert };
  });

/** Entsperrte Ansicht: KPI-Kopf, Verteilung und Orts-Karten. */
function Uebersicht() {
  const orte = useSelector(selectInvestitionen);
  const buchungen = useSelector(selectBuchungen);
  const depotWerte = useSelector(selectDepotWerte);
  const status = useSelector(selectInvestitionenStatus);
  const [neuerOrt, setNeuerOrt] = useState(null);

  const zeilen = useMemo(
    () => kennzahlenJeOrt(orte, buchungen, depotWerte),
    [orte, buchungen, depotWerte]
  );
  const nichtZugeordnet = useMemo(() => summen(buchungenOhneOrt(buchungen)).saldo, [buchungen]);

  const gesamt = useMemo(() => {
    const bar = zeilen.reduce((s, z) => s + z.bar, 0) + nichtZugeordnet;
    const investiert = zeilen.reduce((s, z) => s + z.investiert, 0);
    const wert = zeilen.reduce((s, z) => s + z.wert, 0);
    return { bar, investiert, wert, gv: wert - investiert, gesamt: bar + wert };
  }, [zeilen, nichtZugeordnet]);

  const verteilung = useMemo(
    () =>
      zeilen
        .map((z) => ({ kategorie: z.ort.institut, wert: z.gesamt }))
        .filter((e) => e.wert > 0)
        .sort((a, b) => b.wert - a.wert),
    [zeilen]
  );

  const neu = () =>
    setNeuerOrt({ institut: '', kategorie: '', waehrung: 'EUR', anfangsbestand: '', link: '', notiz: '', positionen: [] });

  return (
    <>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-ink">📊 Finanzen</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Barvermögen und Investitionen je Ort · Ende-zu-Ende verschlüsselt.
          </p>
        </div>
        <button
          type="button"
          onClick={neu}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500"
        >
          + Investitionsort
        </button>
      </header>

      <ModulTabs />

      {status === 'loading' && orte.length === 0 && <p className="text-ink-muted">Lade und entschlüssle…</p>}

      <KpiKopf gesamt={gesamt} gvProz={gesamt.investiert > 0 ? (gesamt.gv / gesamt.investiert) * 100 : null} />

      {verteilung.length > 0 && (
        <div className="mb-6 rounded-2xl border border-line bg-paper-soft p-5">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-muted">
            Vermögen je Investitionsort
          </h2>
          <KategorieDonut kategorien={verteilung} />
        </div>
      )}

      {status !== 'loading' && orte.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
          Noch kein Investitionsort. Lege deinen ersten an (z. B. Trade Republic, Girokonto, Bargeld) – jede
          Ausgabe und Einnahme wird dann einem Ort zugeordnet.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {zeilen.map((z) => (
            <OrtKarte key={z.ort.uuid} zeile={z} />
          ))}
        </div>
      )}

      {Math.abs(nichtZugeordnet) > 0.005 && (
        <p className="mt-4 rounded-xl border border-dashed border-line bg-paper-soft px-4 py-3 text-sm text-ink-muted">
          Nicht zugeordnet: <span className="font-semibold text-ink">{formatKurs(nichtZugeordnet)}</span> aus Buchungen
          ohne Investitionsort. Öffne sie im Reiter „Finanzen“, um sie einem Ort zuzuweisen.
        </p>
      )}

      {neuerOrt && <InvestitionForm investition={neuerOrt} onClose={() => setNeuerOrt(null)} />}
    </>
  );
}

/** KPI-Kacheln: Gesamtvermögen, Barvermögen und Investiert (Wert + G/V). */
function KpiKopf({ gesamt, gvProz }) {
  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-3">
      <div className="rounded-2xl border border-line bg-paper-soft p-5">
        <p className="text-xs uppercase tracking-wide text-ink-muted">Gesamtvermögen</p>
        <p className="mt-1 font-serif text-2xl font-semibold text-ink">{formatKurs(gesamt.gesamt)}</p>
      </div>
      <div className="rounded-2xl border border-line bg-paper-soft p-5">
        <p className="text-xs uppercase tracking-wide text-ink-muted">Barvermögen</p>
        <p className="mt-1 font-serif text-2xl font-semibold text-ink">{formatKurs(gesamt.bar)}</p>
      </div>
      <div className="rounded-2xl border border-line bg-paper-soft p-5">
        <p className="text-xs uppercase tracking-wide text-ink-muted">Investiert (Wert)</p>
        <p className="mt-1 font-serif text-2xl font-semibold text-ink">{formatKurs(gesamt.wert)}</p>
        <div className="mt-2 flex items-center justify-between text-sm text-ink-muted">
          <span>Eingesetzt {formatKurs(gesamt.investiert)}</span>
          <GewinnBadge gv={gesamt.investiert > 0 ? gesamt.gv : null} gvProz={gvProz} />
        </div>
      </div>
    </div>
  );
}

/** Eine Orts-Karte: Barvermögen, Investiert und Gesamt, verlinkt zur Detailseite. */
function OrtKarte({ zeile }) {
  const { ort, bar, investiert, wert, gv, gvProz, gesamt } = zeile;
  const anzahl = (ort.positionen || []).length;
  return (
    <Link
      to={`/projekte/finanzen/ort/${ort.uuid}`}
      className="block rounded-2xl border border-line bg-paper p-5 transition-colors hover:border-brand-400 hover:bg-brand-50/30"
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-serif text-lg font-semibold text-ink">{ort.institut}</p>
          <p className="text-xs text-ink-muted">
            {ort.kategorie || 'Ohne Kategorie'} · {anzahl} {anzahl === 1 ? 'Position' : 'Positionen'}
          </p>
        </div>
        <span className="shrink-0 font-serif text-lg font-semibold text-ink">{formatKurs(gesamt)}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 border-t border-line pt-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-ink-muted">Barvermögen</p>
          <p className="font-serif text-base font-semibold text-ink">{formatKurs(bar)}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-ink-muted">Investiert</p>
          <p className="font-serif text-base font-semibold text-ink">{formatKurs(wert)}</p>
          {investiert > 0 && (
            <div className="mt-0.5">
              <GewinnBadge gv={gv} gvProz={gvProz} />
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
